import { useQuery } from '@tanstack/react-query';
import * as d3 from 'd3';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { feature } from 'topojson-client';
import { topology } from 'topojson-server';
import { presimplify, simplify } from 'topojson-simplify';

import { STATE_NAME_ABBREVIATION_MAP } from '#shared';

import {
  COMPRESSION_LEVELS,
  type ECompressionLevel,
  getCompressionLevelForScale,
} from '../compression';
import { COUNTRY_LABEL_OVERRIDES, TMDX_SERVED_AREAS } from '../constants';
import { EMapLayer } from '../layer.enum';

import type { FeatureCollection, Geometry, Polygon, Position } from 'geojson';
import type { Objects, Topology } from 'topojson-specification';

const TOPO_TTL = 3600_000 * 24 * 7; // 7 days

/**
 * Centroid for placing a country/state label. For multi-polygon features
 * (mainland + far-flung islands or overseas territories) this uses the centroid
 * of the largest polygon only — otherwise the area-weighted whole-feature
 * centroid gets dragged off the mainland (e.g. France's label landing in Spain
 * because of French Guiana, Norway's pulled by Svalbard, etc.).
 */
const labelCentroid = (feature: GeoJSON.Feature): [number, number] => {
  const { geometry } = feature;
  if (geometry?.type === 'MultiPolygon') {
    let largest: Position[][] | undefined;
    let largestArea = -Infinity;
    for (const coordinates of geometry.coordinates) {
      const area = d3.geoArea({ type: 'Polygon', coordinates } as Polygon);
      if (area > largestArea) {
        largestArea = area;
        largest = coordinates;
      }
    }
    if (largest) {
      return d3.geoCentroid({ type: 'Polygon', coordinates: largest });
    }
  }
  return d3.geoCentroid(feature);
};

const fetchJson = async <T>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
  }

  // If the server set Content-Encoding: gzip, the browser already decompressed
  // and response.json() works directly. On S3/CloudFront without Content-Encoding
  // metadata, the raw gzip bytes arrive — decompress manually via DecompressionStream.
  const contentEncoding = response.headers.get('Content-Encoding');
  if (contentEncoding) {
    return response.json();
  }

  const ds = new DecompressionStream('gzip');
  const decompressed = response.body!.pipeThrough(ds);
  const text = await new Response(decompressed).json();
  return text;
};

export const useTopography = () => {
  const { data: usData } = useQuery({
    queryKey: ['statesTopo'],
    queryFn: () =>
      fetchJson<GeoJSON.FeatureCollection<Geometry, { NAME: string }>>(
        '/data/states.json.gz',
      ),
    gcTime: TOPO_TTL,
    retry: 1,
  });

  const { data: countriesData } = useQuery({
    queryKey: ['countriesData'],
    queryFn: () =>
      fetchJson<
        GeoJSON.FeatureCollection<
          Geometry,
          { ['ISO3166-1-Alpha-3']: string; name: string }
        >
      >('/data/countries.geojson.gz'),
    gcTime: TOPO_TTL,
    retry: 1,
  });

  const { data: earthDataTopo } = useQuery({
    queryKey: ['earthDataTopo'],
    queryFn: () => fetchJson<TopoJSON.Topology>('/data/earth-topo.json.gz'),
    gcTime: TOPO_TTL,
    retry: 1,
  });

  const [isTopoReady, setIsTopoReady] = useState(false);

  const topographyRef = useRef<Map<ECompressionLevel, FeatureCollection>>(
    new Map(),
  );
  const getTopography = useCallback(
    (scale: number) => {
      const compression = getCompressionLevelForScale(scale);
      return topographyRef.current.get(compression.name) || null;
    },
    [topographyRef],
  );
  useEffect(() => {
    // Only proceed if we have at least one topology
    if (
      countriesData?.features &&
      usData?.features &&
      earthDataTopo?.objects.lakes_110m
    ) {
      const allFeatures: GeoJSON.Feature[] = [];

      countriesData.features.forEach((feature, index) => {
        const isTMDXServed = TMDX_SERVED_AREAS.includes(
          feature.properties.name,
        );
        allFeatures.push({
          ...feature,
          properties: {
            ...feature.properties,
            __layer: isTMDXServed ? EMapLayer.tmdxServed : EMapLayer.countries,
            __layerIndex: isTMDXServed ? 1 : 0,
            __featureIndex: index,
          },
        });
      });
      usData.features.forEach((feature, index) => {
        allFeatures.push({
          ...feature,
          properties: {
            ...feature.properties,
            __layer: EMapLayer.tmdxServed,
            __layerIndex: 1,
            __featureIndex: index,
          },
        });
      });

      const lakes = feature(
        earthDataTopo,
        earthDataTopo.objects.lakes_110m,
      ) as GeoJSON.FeatureCollection;
      lakes.features.forEach((feature, index) => {
        allFeatures.push({
          ...feature,
          properties: {
            ...feature.properties,
            __layer: EMapLayer.lakes,
            __layerIndex: 2,
            __featureIndex: index,
          },
        });
      });

      // Create combined GeoJSON
      const combinedGeoJSON: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: allFeatures,
      };

      try {
        // Convert to TopoJSON for compression
        const topojsonData = topology({ merged: combinedGeoJSON });

        // Apply presimplification and simplification for compression
        const topoPresimplified = presimplify(
          topojsonData as unknown as Topology<Objects>,
        );

        for (const level of Object.values(COMPRESSION_LEVELS)) {
          const topoSimplified = simplify(topoPresimplified, level.threshold);

          // Convert back to GeoJSON
          const compressedGeoJSON = feature(
            topoSimplified,
            topoSimplified.objects.merged,
          ) as GeoJSON.FeatureCollection;
          topographyRef.current.set(level.name, compressedGeoJSON);
        }
        setTimeout(() => setIsTopoReady(true), 0);
      } catch (error) {
        console.error('Failed to compress merged topology:', error);
      }
    }
  }, [countriesData, usData, earthDataTopo]);

  const topoLabels = useMemo(() => {
    if (!usData || !countriesData) {
      return;
    }
    return [
      ...usData.features
        .map(feature => {
          const centroid = labelCentroid(feature);
          return {
            feature,
            centroid,
            text: STATE_NAME_ABBREVIATION_MAP[feature.properties.NAME],
          };
        })
        .filter(label => label.text),
      ...countriesData.features
        .filter(feature => TMDX_SERVED_AREAS.includes(feature.properties.name))
        .map(feature => {
          const centroid = labelCentroid(feature);
          const { name } = feature.properties;
          const isoAlpha3 = feature.properties['ISO3166-1-Alpha-3'];
          return {
            feature,
            centroid,
            text: COUNTRY_LABEL_OVERRIDES[name] ?? isoAlpha3,
          };
        }),
    ];
  }, [usData, countriesData]);

  // This hook is a placeholder for future topology-related logic
  // Currently, it does not perform any operations or return any values
  // It can be extended later to include topology data fetching or processing
  return {
    usData,
    getTopography,
    isTopoReady,
    topoLabels,
    countriesData,
  };
};
